/**
 * modele.ts, LA FORME D'UNE APP TELLE QUE LE STUDIO LA LIT.
 *
 * CE FICHIER NE LIT RIEN, ET C'EST TOUT SON INTERET : il ne porte que des
 * TYPES et deux gardes de type. Il decrit la forme d'une app (sa fiche, son
 * fichier de champs, son fichier d'ecrans) telle que les modules de
 * projection et d'edition la consomment, qu'elle vienne du DEPOT par le pont
 * DEV (`src/dev/studio/lecture.ts`) ou du DOCUMENT Firestore d'une org
 * (`src/studio/client/`). Ces types vivaient dans `lecture.ts`, avec les
 * appels au pont Vite : le studio client aurait importe la greffe DEV rien
 * que pour une forme.
 *
 * LA FORME EST REDECLAREE ICI plutot qu'importee de `@taki/data-model`, et
 * c'est la raison qui vaut partout dans le shell : ce package n'a pas le
 * modele de donnees dans ses dependances. Le schema qui fait foi vit dans
 * `packages/data-model/src/app-screens.ts` et `custom-field.ts`, la CI le
 * rejoue a chaque ecriture, et une declaration validee par Zod ne compile
 * contre ces formes que si les deux concordent.
 */
/** Ce qu'une valeur lue porte toujours : sa valeur, et d'ou elle vient. */
export interface Lu<T> {
  valeur: T;
  /** Le chemin du fichier d'ou ca vient, ou `null` si rien n'a ete trouve. */
  source: string | null;
  /**
   * L'empreinte des octets lus, quand le bloc est ECRIVABLE. Elle repart avec
   * l'ecriture : si le fichier a bouge entre-temps, le pont refuse plutot que
   * d'ecraser. Absente sur les blocs qui ne s'ecrivent pas.
   */
  empreinte?: string | null;
}

/** Une porte, telle que le tableau `doors` de l'ecran la declare. */
export interface PorteLue {
  id: string | null;
  label: string | null;
  ordre: number;
  /** Les clefs que la porte declare, hors `id` et `label`. */
  declare: string[];
}

/** Un champ personnalise, reconnu par son ecriture `writeCustomField`. */
export interface ChampLu {
  cle: string;
  label: string | null;
  /** `select`, `number`, `date`... ou `null` : un champ texte ne declare rien. */
  kind: string | null;
}

/** Une carte du plan de fiche. */
export interface CarteLue {
  titre: string | null;
  cles: string[];
  /** Comment la carte occupe sa rangee, dans les mots du fichier d'app. */
  disposition: 'demi' | 'pleine' | 'titre-pleine';
}

/**
 * L'entree de registre d'une app : ce que le registre de l'hote ajoute a la
 * fiche, et rien qui s'y trouve deja. Le libelle n'en est plus, l'entree
 * IMPORTE la fiche dont le shell derive libelle et icone.
 */
export interface RegistreLu {
  reglages: boolean;
  actions: { id: string; label: string; iconName: string }[];
}

/** Ce que le pont ne sait pas lire, dit nommement. */
export interface AngleMort {
  quoi: string;
  pourquoi: string;
}

/** La fiche d'identite, telle quelle. Le pont ne la reforme pas. */
export type FicheLue = Record<string, unknown>;

/** Un champ declare, tel que le fichier de l'app le porte. */
export interface ChampDeclareLu {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  /** L'exemple rendu dans une case VIDE (« ex. : Sony FX9 n°3 »). */
  placeholder?: string;
  options?: { value: string; label: string; tone?: string }[];
  helpText?: string;
  /** La cible d'un champ `lien` : la famille visee, son type, le champ qui la nomme. */
  reference?: ReferenceLue;
}

/**
 * LA CIBLE D'UN LIEN, telle que `fieldReferenceSchema` la fixe cote data-model :
 * une famille du socle, un type local, et le champ de la fiche visee a lire
 * comme titre (`socle:<natif>` ou une clef du sac). Portee par un champ du sac
 * (`ChampDeclareLu`) ou par une colonne NATIVE du catalogue (`ColonneLue`).
 */
export interface ReferenceLue {
  family: string;
  type?: string;
  titre?: string;
  sousTitre?: string;
  /** Les fiches que le selecteur propose : une condition du socle, lue dans la famille visee. */
  choix?: unknown;
}

/** Le contenu d'un `taki.fields.json`, tel qu'il est sur le disque. */
export interface DeclarationLue {
  version: number;
  jeux: {
    family: string;
    type?: string;
    champs: ChampDeclareLu[];
    /** LES FICHIERS JOINTS du jeu : absente, la capacite est coupee. */
    fichiers?: { types?: string[]; tailleMaxMo?: number };
  }[];
}

/* ------------------------------------------------------------------ */
/* Le fichier d'ecrans : la structure d'un ecran, devenue de la donnee  */
/* ------------------------------------------------------------------ */

/*
 * LA FORME EST REDECLAREE ICI, comme celle du fichier de champs juste
 * au-dessus, et pour la meme raison : ce module decrit ce que le PONT rend, pas
 * ce que Zod valide. Le schema qui fait foi vit dans `@taki/data-model`
 * (`app-screens.ts`) et la CI le rejoue a chaque ecriture ; ici on type ce qui
 * traverse le reseau, sans tirer le modele de donnees dans une greffe de dev.
 */

/** D'ou sort la valeur d'un role : un champ, une liste de champs, ou une fonction. */
export interface SourceLue {
  champ?: string;
  champs?: string[];
  fn?: string;
  vide?: string;
  catalogue?: string;
  habillage?: string;
  modifiable?: boolean;
  /** Lu sur le role `title` : l'exemple rendu quand la fiche neuve n'a pas de titre. */
  placeholder?: string;
}

/**
 * UNE ENTREE DE CARTE, forme longue : le champ et son VERROU. Le nom nu suffit
 * et vaut modifiable ; cette forme ne sert qu'a poser `modifiable: false`.
 */
export interface ChampDeCarteLu {
  champ: string;
  modifiable?: boolean;
}

/**
 * UN CHAMP DE TOTAL : une entree de carte qui ne lit AUCUN champ de cette
 * fiche, mais COMPTE les fiches liees. Son `champ` appartient au jeu LIE.
 */
export interface TotalDeCarteLu {
  libelle: string;
  liee: string;
  total: string;
  champ?: string;
  /** La ponderation d'une somme : le champ de reference du jeu lie, et le champ numerique de la fiche qu'il designe. */
  fois?: { par: string; champ: string };
}

/** Une entree de carte : le nom nu, le nom et son verrou, ou un total. */
export type EntreeDeCarteLue = string | ChampDeCarteLu | TotalDeCarteLu;

/** Une carte de fiche qui montre des CHAMPS : son titre, ses entrees, sa disposition. */
export interface CarteDeChampsLue {
  titre: string;
  champs: EntreeDeCarteLue[];
  /** `demi` (le defaut, absente), `pleine` ou `titre-pleine` : le fichier n'ecrit pas le defaut. */
  disposition?: string;
  /** `principal` (le defaut, absente) ou `droite` : la colonne de droite, en haut de la fiche. */
  placement?: string;
  /** JAMAIS posee ici : c'est par elle que les deux formes se distinguent. */
  liee?: undefined;
  /** JAMAIS posee ici : c'est la marque de la carte des fichiers joints. */
  fichiers?: undefined;
}

/**
 * UNE CARTE LIEE : elle monte une VUE du socle sur les fiches d'une autre
 * porte, et elle ne montre AUCUN champ de cette fiche.
 */
export interface CarteLieeLue {
  titre: string;
  /** L'id d'un LIEN de la porte qui porte cette fiche. */
  liee: string;
  /** `core.liste` ou `core.tableau` ; le pont relaie, il ne valide pas. */
  vue?: string;
  plafond?: number;
  /**
   * Les colonnes du tableau de la carte (`core.tableau` seulement), des noms du
   * catalogue de colonnes de l'ecran. Facultatif : absent, la carte rend les
   * colonnes de la porte liee. Lu ici pour qu'une copie de carte ne le perde pas.
   */
  colonnes?: string[];
  /** JAMAIS posee ici : une carte liee n'a pas de champs a lister. */
  champs?: undefined;
  /** JAMAIS posee ici : c'est la marque de la carte des fichiers joints. */
  fichiers?: undefined;
}

/**
 * LA CARTE DES FICHIERS JOINTS : la section des fichiers de la fiche, a son
 * rang. Ni champ ni lien : la capacite se lit sur le JEU (`jeux[].fichiers`),
 * cette carte n'en est que la presentation (`appScreenFilesCardSchema`).
 */
export interface CarteFichiersLue {
  /** Facultatif, et rare : le fichier peut en poser un, le Studio le relaie. */
  id?: string;
  titre: string;
  fichiers: true;
  /** `false` : lecture seule. Absente : elle suit les droits de la porte. */
  modifiable?: false;
  /** L'affichage d'ouverture : une taille de vignettes. Absente : la liste, le defaut du socle. */
  vignettes?: 'petites' | 'moyennes' | 'grandes';
  /** JAMAIS posee ici : elle n'a pas de champs a lister. */
  champs?: undefined;
  /** JAMAIS posee ici : elle ne cite aucun lien. */
  liee?: undefined;
}

/**
 * UNE CARTE DE FICHE, ET SES DEUX FORMES EXCLUSIVES.
 *
 * ELLES SONT TYPEES SEPAREMENT PARCE QU'ELLES N'ONT PAS LES MEMES CLEFS, et
 * c'est ce qu'un seul objet a `champs` obligatoire faisait mentir : le Studio
 * ouvert sur une app qui declare une carte liee jetait « Cannot read properties
 * of undefined (reading 'map') » (`lignesFiche`, `projectionEcrans.ts`). Le
 * schema qui fait foi dit deja l'union (`appScreenCardSchema`,
 * packages/data-model/src/app-screens.ts) ; le pont la redit ici, pour que le
 * compilateur refuse une liste qu'aucune des deux formes ne promet.
 */
export type CarteDeclareeLue = CarteDeChampsLue | CarteLieeLue | CarteFichiersLue;

/** La carte montre-t-elle les fiches LIEES plutot que des champs ? */
export const estCarteLieeLue = (carte: CarteDeclareeLue): carte is CarteLieeLue =>
  carte.liee !== undefined;

/** La carte est-elle celle des FICHIERS joints ? */
export const estCarteFichiersLue = (carte: CarteDeclareeLue): carte is CarteFichiersLue =>
  carte.fichiers === true;

/**
 * La carte montre-t-elle les CHAMPS de la fiche ? C'est la garde a employer
 * avant de lire `champs` : « pas liee » ne suffit plus depuis la troisieme forme.
 */
export const estCarteDeChampsLue = (carte: CarteDeclareeLue): carte is CarteDeChampsLue =>
  carte.liee === undefined && carte.fichiers === undefined;

/** L'entree est-elle un CHAMP DE TOTAL plutot qu'un champ de cette fiche ? */
export const estTotalDeCarteLu = (entree: EntreeDeCarteLue): entree is TotalDeCarteLu =>
  typeof entree !== 'string' && 'total' in entree;

/**
 * LE NOM NU D'UNE ENTREE DE CARTE, ou `null` quand l'entree ne nomme aucun
 * champ de CETTE fiche. Le cas est celui du champ de total : son `champ`
 * appartient au jeu LIE, et le rendre ici le ferait chercher dans les colonnes
 * de la vue courante, ou il n'a aucune raison d'etre.
 */
const nomDeChampDeCarteLu = (entree: EntreeDeCarteLue): string | null => {
  if (typeof entree === 'string') return entree;
  return estTotalDeCarteLu(entree) ? null : entree.champ;
};

/**
 * LES CHAMPS QU'UNE CARTE NOMME SUR CETTE FICHE, toujours un tableau : vide sur
 * une carte liee, vide sur une carte dont toutes les entrees sont des totaux.
 * C'est LA lecture des champs d'une carte ; il n'y en a pas d'autre, sans quoi
 * chaque appelant redeciderait ce qu'il fait des deux formes.
 */
export const nomsDeChampsDeCarteLue = (carte: CarteDeclareeLue): string[] =>
  estCarteDeChampsLue(carte)
    ? carte.champs.map(nomDeChampDeCarteLu).filter((nom): nom is string => nom !== null)
    : [];

/** Le detail d'une vue montee : ses roles, ses colonnes, ses cartes. */
export interface BindingLu {
  title?: SourceLue;
  subtitle?: SourceLue;
  avatar?: SourceLue;
  status?: SourceLue;
  badges?: SourceLue;
  groupKey?: SourceLue;
  lane?: SourceLue;
  dateStart?: SourceLue;
  dateEnd?: SourceLue;
  extra?: Record<string, SourceLue>;
  colonnes?: string[];
  cartes?: CarteDeclareeLue[];
  /** Les totaux lies par ligne (tableau, kanban) : `{ libelle, liee, total, champ? }`. */
  totauxLies?: { libelle: string; liee: string; total: string; champ?: string }[];
  /**
   * La surbrillance des cartes (kanban) : `{ si, couleur, etiquette? }`, `si`
   * valant `nouveau` ou une condition du socle (`docs/socle/cartes-kanban.md`).
   */
  surbrillances?: { si: unknown; couleur: string; etiquette?: string }[];
  /**
   * Les blocs du tableau de bord (`core.bord`), dans l'ordre : `{ type: 'chiffres' | 'etapes' | 'liste', ... }`
   * (`docs/socle/tableau-de-bord.md`). Lus tels que le fichier les porte.
   */
  blocs?: { type: string; libelle?: string; [clef: string]: unknown }[];
  motDuTitre?: string;
  motDuStatut?: string;
}

/** Une colonne du catalogue de l'ecran. */
export interface ColonneLue {
  champ: string;
  label?: string;
  type?: string;
  triable?: boolean;
  total?: string;
  habillage?: string;
  /**
   * L'exemple d'un champ NATIF de la famille : `taki.fields.json` ne le connait
   * pas, donc c'est sa colonne du catalogue qui porte son placeholder.
   */
  placeholder?: string;
  /** La cible d'une colonne native de type `lien` (`projects.contactId` -> contacts). */
  reference?: ReferenceLue;
  /** Le catalogue de valeurs d'une colonne NATIVE a choix (`socle:status`). */
  options?: { value: string; label: string; tone?: string; archived?: boolean }[];
}

/** La carte d'identite d'une vue 100% custom. */
export interface VueCustomLue {
  id: string;
  label: string;
  niveau?: string;
  exige?: string[];
  rolesEnPlus?: string[];
  demande?: string;
  defilement?: string;
  /** Le jeu d'une vue de FICHE (`niveau: 'record'`) : elle montre une fiche de ce jeu. */
  jeu?: { family: string; type?: string };
}

/**
 * UNE VUE HTML PUBLIEE (`ecran.vuesHtml`, `VueHtmlDeclaree` de
 * `@taki/data-model`), telle que le Studio la lit : il la liste, la controle
 * et la retire, jamais il ne la reecrit.
 */
export interface VueHtmlLue {
  id: string;
  label: string;
  jeu: { family: string; type?: string };
  champs: string[];
  liens?: string[];
  html: string;
  version: number;
  publieeLe: string;
  publieePar: string;
}

/** Une clef de tri : un champ, ou l'un des cinq roles que le socle trie seul. */
export interface ClefDeTriLue {
  champ?: string;
  role?: string;
}

/** Une entree du catalogue de tri : le libelle n'est porte que par un role. */
export interface OptionDeTriLue extends ClefDeTriLue {
  label?: string;
}

/** Un niveau de la pile de tri : une clef, un sens. */
export interface NiveauDeTriLu extends ClefDeTriLue {
  sens: 'asc' | 'desc';
}

/** Le catalogue de tri d'un ecran, et sa pile d'arrivee. */
export interface TriLu {
  options: OptionDeTriLue[];
  defaut?: NiveauDeTriLu[];
}

/** Le catalogue de filtres d'une porte. */
export interface FiltresLus {
  parChamp?: string;
  autres?: { id: string; label: string; fn?: string; condition?: unknown }[];
}

/** Un favori preset : un etat {filtres, pile de tri} nomme. */
export interface FavoriLu {
  id: string;
  label: string;
  filtres: string[];
  tri: NiveauDeTriLu[];
}

/** La fenetre qu'une action demande avant de se faire. */
export interface ConfirmationLue {
  ton: 'neutral' | 'danger';
  titre: string;
  corps: string;
  faire: string;
  annuler?: string;
}

/** Une action de la rangee 3a : tout est de la donnee sauf son effet, nomme. */
export interface ActionLue {
  id: string;
  label: string;
  kind?: 'add';
  variant?: 'primary' | 'secondary';
  /**
   * FACULTATIF SUR UN AJOUT, ET C'EST LA REGLE DU 09/09/2026 : une action
   * `kind: 'add'` sans effet est tenue par le SOCLE (fiche vide en zone 5,
   * regle G6). Partout ailleurs, le schema l'exige encore.
   */
  fn?: string;
  /** `'fiche'` : un geste de la fiche ouverte, que la rangee 3a ne rend pas (`appScreenActionSchema`). */
  niveau?: 'collection' | 'fiche';
  /** La fenetre d'app d'un geste de fiche, a la place de `fn`. */
  fenetre?: string;
  /** Action FLUX : le clic lance les flux actifs branches sur ce bouton (`docs/socle/smart-boutons.md`). */
  flux?: true;
  /** La condition de disponibilite (niveau fiche), une `Condition` du socle. */
  si?: unknown;
  /** Condition fausse : `griser` (defaut) ou `masquer`. */
  siFaux?: 'griser' | 'masquer';
  /** La raison du grise, a la place de la phrase derivee de `si`. */
  raison?: string;
  confirmation?: ConfirmationLue;
}

/** Une stat de la zone 2c : un champ, un calcul nomme, un mot. */
export interface StatLue {
  id: string;
  label: string;
  calcul: string;
  champ?: string;
  valeur?: string;
  habillage?: string;
}

/** Ce qu'une fiche NEUVE porte deja, sur cette porte. */
export interface CreationLue {
  /** Nom du champ -> sa valeur : la clef nue d'un declare, `socle:<natif>` sinon. */
  defauts?: Record<string, string | number | boolean>;
}

/**
 * CE QUE LA PORTE DIT quand son jeu est VIDE, ou quand la fiche demandee
 * n'existe pas : deux phrases de l'app. Absentes, le socle parle.
 */
export interface EtatDePorteLu {
  titre: string;
  indication?: string;
}

/**
 * UN LIEN vers une AUTRE porte du meme ecran : trois noms, jamais une requete.
 *
 * IL EST LU ICI PARCE QU'IL SE CASSE : retirer la porte qu'il vise laisse un
 * lien qui ne pointe plus nulle part, et le schema refuse alors tout le
 * fichier, avec un message que personne ne relie au geste. Le moteur d'edition
 * l'elague donc lui-meme (`ecransEdition.ts`).
 */
export interface LienLu {
  id: string;
  /** L'id de l'AUTRE porte du meme ecran. */
  porte: string;
  /** Le champ du jeu lie qui porte l'id de la fiche parente. Exclusif de `liste`. */
  par?: string;
  /** Le sens direct : le champ LISTE de la fiche ouverte qui cite les fiches liees. Exclusif de `par`. */
  liste?: string;
}

/** Une porte declaree. */
/** Un en-tete de la rangee 3 tel que le fichier l'ecrit (`EnTeteDeFiche` de `@taki/data-model`, redeclare ici). */
export interface VerrouLu {
  si: unknown;
  avant?: string;
  depuis?: string;
  raison: string;
}

export interface EnTeteLu {
  champ: string;
  mode: 'flux' | 'selection';
  verrou?: VerrouLu | VerrouLu[];
}

export interface PorteDeclareeLue {
  id: string;
  label: string;
  /** Sous-catalogue de vues de cette porte. Absent : celui de l'ecran. */
  vues?: string[];
  arrivee?: string;
  placeholder?: string;
  vide?: EtatDePorteLu;
  introuvable?: EtatDePorteLu;
  modeles?: boolean;
  recurrences?: boolean;
  /** `true` : le tableau de la porte s'edite en place. Absent : non. */
  modifierDansLeTableau?: boolean;
  tranchable?: boolean;
  /** `false` : hors navigation, on n'y entre que par un lien. Absent : oui. */
  navigation?: boolean;
  jeu?: { nom: string; family: string; type?: string };
  canvas?: { elements?: string[]; keeps?: string[] };
  /** La condition qui limite la porte a une part de son jeu. */
  tranche?: unknown;
  filtres?: FiltresLus;
  favoris?: FavoriLu[];
  actions?: ActionLue[];
  creation?: CreationLue;
  stats?: StatLue[];
  liens?: LienLu[];
  /**
   * Au clic sur une fiche : entrer dans la porte du lien `lien`, dans la vue
   * `vue` ; ou ouvrir la fiche dans une vue de fiche ; ou dans une autre porte
   * (`docs/socle/fiche-suivi.md` section 6) ; ou dans la derniere vue de
   * fiche que la personne a utilisee sur cette porte (`derniereVue`).
   */
  auClic?: { lien: string; vue?: string } | { vueDeFiche: string } | { derniereVue: true } | { porte: string; app?: string };
  /**
   * « En tête de fiche » : les champs a valeurs de la rangee 3 et leur mode,
   * dans l'ordre (la liste, forme canonique). L'ancien objet seul reste LU, et
   * vaut une liste d'un : on ne le lit que par `enTetesDeLaPorte`. Un en-tete peut
   * porter des verrous (`etapes-verrou`, un objet seul ou une liste) : `si` une condition du
   * socle, `avant` ou `depuis` une valeur du champ, `raison` quelques mots.
   */
  etapes?: EnTeteLu[] | EnTeteLu;
  /** Les vues de fiche montrees, hors « Fiche » (absent : les vues HTML du jeu, aucune vue Canvas). */
  vuesFiche?: string[];
  /** La porte offre « Fiche personnalisee » (`idDeFichePerso`), meme sans HTML encore. Jamais `false` : la clef part. */
  fichePerso?: true;
  /** Le detail par vue de CETTE porte, quand il n'est pas celui de l'ecran. */
  vb?: Record<string, BindingLu | undefined>;
}

/** Un ecran declare. */
export interface EcranLu {
  id: string;
  vues: string[];
  vuesCustom?: VueCustomLue[];
  vuesHtml?: VueHtmlLue[];
  arrivee: string;
  porteArrivee?: string;
  colonnes?: ColonneLue[];
  vb: Record<string, BindingLu | undefined>;
  tri?: TriLu;
  verbes?: { archive?: boolean; unarchive?: boolean; remove?: boolean };
  portes: PorteDeclareeLue[];
}

/** Le contenu d'un `taki.screens.json`, tel qu'il est sur le disque. */
export interface EcransLus {
  version: number;
  ecrans: EcranLu[];
}

export interface AppLue {
  id: string;
  identite: Lu<FicheLue>;
  registre: Lu<RegistreLu | null>;
  portes: Lu<PorteLue[]>;
  vues: Lu<{ type: string; source: string }[]>;
  champs: Lu<ChampLu[]>;
  /**
   * LES CHAMPS DECLARES, la seule partie ECRIVABLE de la structure d'une app.
   * `valeur` est `null` quand l'app n'est pas encore migree : c'est un fait, pas
   * une faute, et l'ecran le dit plutot que d'afficher une liste vide.
   */
  declares: Lu<DeclarationLue | null>;
  /**
   * LES ECRANS DECLARES, la seconde partie ECRIVABLE de la structure. `null`
   * quand l'app n'a pas encore de `taki.screens.json` : ses portes et ses vues
   * se lisent alors par motifs dans le TypeScript, et ne se modifient pas.
   */
  ecrans: Lu<EcransLus | null>;
  cartes: Lu<CarteLue[]>;
  /**
   * LE TEXTE A COLLER A L'AGENT, deduit par le pont des fichiers reels de l'app
   * (`texteDeRelais`, `brief/relais.mjs`). Absent hors du pont (une instance
   * cliente n'a pas de dossier) : l'ecran ne propose alors aucun relais.
   */
  relais?: string;
  nonLisible: AngleMort[];
}

export interface AppListee {
  id: string;
  nom: string | null;
  version: string | null;
  statut: string | null;
  /** L'app est-elle montee par le registre de l'hote. */
  monte: boolean;
  fiche: string;
  /**
   * LES FAMILLES QUE SA FICHE DECLARE (`entities` de `taki.app.json`), avec
   * leurs sous-types : ce qu'un « champ lié » du Studio peut viser dans une
   * autre app (`famille:<id>[/<type>]`).
   */
  entities: { name: string; types: string[] }[];
  /**
   * SES PORTES DECLAREES ET LEUR JEU (`taki.screens.json`), pour « Ouvrir dans
   * <app> › <porte> » au clic sur une fiche d'une app en construction. Absent
   * d'un pont plus ancien : rien n'est offert.
   */
  portes?: { id: string; label: string; jeu: { family: string; type?: string } }[];
}

export interface Inventaire {
  apps: AppListee[];
  /**
   * LE GARDE-FOU DE MUTISME. `aveugle` vaut vrai quand des apps existent mais
   * qu'AUCUNE ne rend de porte : c'est le signe que le motif de lecture ne
   * correspond plus au code, pas que le depot est vide. Sans ce signal, un
   * renommage cote app rendrait le studio silencieusement faux.
   */
  sonde: { appsLues: number; appsAvecPortes: number; aveugle: boolean };
}

/** Une lecture qui n'a pas abouti, avec sa raison affichable. */
export interface Panne {
  panne: string;
}

export type Resultat<T> = T | Panne;

export const estPanne = <T>(r: Resultat<T>): r is Panne =>
  typeof (r as Panne).panne === 'string';

/**
 * LES CLEFS DE FICHE QUI SE RENOMMENT. Liste fermee : le pont DEV les accepte
 * a l'ecriture (`lecture.ts`), et le studio client n'en ouvre qu'un
 * sous-ensemble (`name`, `description`, `icon`, `menuLabel` : les quatre que
 * les rules et le schema du SDK laissent poser par une org).
 */
export type ClefFiche = 'name' | 'description' | 'category' | 'icon' | 'status' | 'menuLabel';
